import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { roleLabel } from './roleLabel';

/**
 * PermissionsPanel — operators + roles + permissions, with mutation.
 *
 * Mutation v1 (this commit):
 *   - Create operator (display_name + identifier + password)
 *   - Assign role to operator
 *   - Revoke role assignment
 *
 * Deferred to a later slug:
 *   - Edit role (add/remove permissions on existing roles)
 *   - Create new (non-network-scoped) role
 *   - Operator delete / password reset
 *
 * Gated at the App.js level by `grant_permission @ cluster=0`; the
 * BFF endpoints re-gate via _require_grant_permission so direct curl
 * access still 403s if the operator lacks the permission.
 */
export default function PermissionsPanel() {
  const [operators, setOperators] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [assigningTo, setAssigningTo] = useState(null);  // operator object or null
  const [linking, setLinking] = useState(null);          // operator object or null
  const [chatUsers, setChatUsers] = useState(null);      // null = not visible to this operator

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ops, rls] = await Promise.all([
        fetch('/api/admin/operators', { credentials: 'same-origin' }),
        fetch('/api/admin/roles', { credentials: 'same-origin' }),
      ]);
      if (!ops.ok) { setError(`/api/admin/operators → HTTP ${ops.status}`); return; }
      if (!rls.ok) { setError(`/api/admin/roles → HTTP ${rls.status}`); return; }
      const opsBody = await ops.json();
      const rlsBody = await rls.json();
      setOperators((opsBody && opsBody.entity) || []);
      setRoles((rlsBody && rlsBody.entity) || []);
      // Chat users need manage_user_access; without it the column shows ids only.
      const u = await fetch('/api/user-access/users', { credentials: 'same-origin' });
      setChatUsers(u.ok ? (((await u.json()) || {}).users || []) : null);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const revokeAssignment = async (assignmentId, roleDisplay, operatorName) => {
    if (!window.confirm(`Revoke ${roleDisplay} from ${operatorName}?`)) return;
    const r = await fetch(`/api/admin/role_assignments/${assignmentId}/revoke`, {
      method: 'POST', credentials: 'same-origin',
    });
    if (!r.ok) {
      window.alert(`revoke failed: HTTP ${r.status}\n${await r.text()}`);
      return;
    }
    refresh();
  };

  const deleteOperator = async (op) => {
    if (!window.confirm(
      `Delete operator "${op.display_name}" (id ${op.id})?\n\n` +
      `Permanently removes their login, sessions, and role assignments. ` +
      `There is no undo.`)) return;
    const r = await fetch(`/api/admin/operators/${op.id}`, {
      method: 'DELETE', credentials: 'same-origin',
    });
    if (!r.ok) {
      // Backend 409s self-delete + last-admin lockout with a clear message.
      window.alert(`delete failed: HTTP ${r.status}\n${await r.text()}`);
      return;
    }
    refresh();
  };

  const unlinkOperator = async (op) => {
    if (!window.confirm(
      `Unlink "${op.display_name}" from ${chatUserLabel(chatUsers, op.account_id)}?\n\n` +
      `Their admin sessions end and they can't sign in with that chat account until ` +
      `relinked.`)) return;
    const r = await fetch(`/api/admin/operators/${op.id}/unlink`, {
      method: 'POST', credentials: 'same-origin',
    });
    if (!r.ok) {
      window.alert(`unlink failed: HTTP ${r.status}\n${await r.text()}`);
      return;
    }
    refresh();
  };

  const deleteRole = async (role) => {
    if (!window.confirm(
      `Delete role "${roleLabel(role.name)}" (id ${role.id})?\n\n` +
      `Removes the role and all its permission grants + assignments. ` +
      `There is no undo.`)) return;
    const r = await fetch(`/api/admin/roles/${role.id}`, {
      method: 'DELETE', credentials: 'same-origin',
    });
    if (!r.ok) {
      window.alert(`delete failed: HTTP ${r.status}\n${await r.text()}`);
      return;
    }
    refresh();
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Permissions</h2>
        <button onClick={refresh} style={refreshBtnStyle}>refresh</button>
      </div>

      {loading && <div style={{ color: '#888' }}>Loading…</div>}
      {error && <div style={{ color: '#c00' }}>{error}</div>}

      {!loading && !error && (
        <>
          <Section
            title="Operators"
            count={operators.length}
            action={
              <button onClick={() => setShowCreate(true)} style={primaryBtnStyle}>
                + new operator
              </button>
            }
          >
            {operators.length === 0 ? (
              <Empty>No operators yet. Run bootstrap_permissions.py to mint the first one.</Empty>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr style={trHeadStyle}>
                    <th style={thStyle}>ID</th>
                    <th style={thStyle}>Display name</th>
                    <th style={thStyle}>Chat account</th>
                    <th style={thStyle}>Roles (live)</th>
                    <th style={thStyle}>Created</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {operators.map((op) => (
                    <tr key={op.id} style={trStyle}>
                      <td style={tdStyle}>{op.id}</td>
                      <td style={tdStyle}>{op.display_name}</td>
                      <td style={tdStyle} data-testid={`op-account-${op.id}`}>
                        {op.account_id != null ? (
                          <>
                            {chatUserLabel(chatUsers, op.account_id)}
                            <button onClick={() => unlinkOperator(op)} style={{ ...smallBtnStyle, marginLeft: 6 }}
                                    data-testid={`op-unlink-${op.id}`}>unlink</button>
                          </>
                        ) : (
                          <>
                            <em style={{ color: '#b07a00' }}>not linked</em>
                            <button onClick={() => setLinking(op)} style={{ ...smallBtnStyle, marginLeft: 6 }}
                                    data-testid={`op-link-${op.id}`}>link</button>
                          </>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {op.roles.length === 0 ? (
                          <em style={{ color: '#999' }}>none</em>
                        ) : (
                          op.roles.map((r) => (
                            <span key={r.assignment_id} style={{ marginRight: 4 }}>
                              <span style={badgeStyle}>{roleLabel(r.role_name)}</span>
                              <button
                                title={`Revoke ${roleLabel(r.role_name)}`}
                                onClick={() => revokeAssignment(r.assignment_id, roleLabel(r.role_name), op.display_name)}
                                style={revokeBtnStyle}
                              >×</button>
                            </span>
                          ))
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: '#888', fontSize: '0.75rem' }}>
                        {op.created_at && new Date(op.created_at).toLocaleString()}
                      </td>
                      <td style={tdStyle}>
                        <button onClick={() => setAssigningTo(op)} style={smallBtnStyle}>
                          + assign role
                        </button>
                        <button onClick={() => deleteOperator(op)}
                                style={{ ...smallBtnStyle, color: '#c00', borderColor: '#e3b4b4', marginLeft: 6 }}
                                title="Delete operator">delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <Section title="Roles" count={roles.length}>
            {roles.length === 0 ? (
              <Empty>No roles yet.</Empty>
            ) : (
              <RolePicker roles={roles} onDeleteRole={deleteRole} />
            )}
          </Section>
        </>
      )}

      {showCreate && (
        <ChatUserModal
          title="New operator"
          intro="Pick the chat user to make an operator. They sign in to admin with their chat account; give them roles afterwards."
          submitLabel="create operator"
          users={chatUsers}
          operators={operators}
          send={(accountId) => fetch('/api/admin/operators', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ account_id: accountId }),
          })}
          onClose={() => setShowCreate(false)}
          onDone={() => { setShowCreate(false); refresh(); }}
        />
      )}
      {linking && (
        <ChatUserModal
          title={`Link "${linking.display_name}" to a chat user`}
          intro="They will sign in to admin with this chat account, and their old admin password stops working. Only link the account you know is theirs."
          submitLabel="link"
          users={chatUsers}
          operators={operators}
          send={(accountId) => fetch(`/api/admin/operators/${linking.id}/link`, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ account_id: accountId }),
          })}
          onClose={() => setLinking(null)}
          onDone={() => { setLinking(null); refresh(); }}
        />
      )}
      {assigningTo && (
        <AssignRoleModal
          operator={assigningTo}
          roles={roles}
          onClose={() => setAssigningTo(null)}
          onAssigned={() => { setAssigningTo(null); refresh(); }}
        />
      )}
    </div>
  );
}


function RolePicker({ roles, onDeleteRole }) {
  // Stable sort by name so the dropdown order doesn't shuffle each refresh.
  // Ultimate / system roles bubble to the top (they're the most-frequently
  // inspected); everything else alphabetical.
  const sorted = useMemo(() => {
    const SYSTEM = new Set(['ultimate']);
    return [...roles].sort((a, b) => {
      const aSys = SYSTEM.has(a.name);
      const bSys = SYSTEM.has(b.name);
      if (aSys !== bSys) return aSys ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [roles]);

  const [selectedId, setSelectedId] = useState(() => sorted[0] ? String(sorted[0].id) : '');

  // If the selected role disappears (deleted), or this is first render
  // with no roles, pick the first available.
  useEffect(() => {
    if (!sorted.find((r) => String(r.id) === selectedId) && sorted[0]) {
      setSelectedId(String(sorted[0].id));
    }
  }, [sorted, selectedId]);

  const selected = sorted.find((r) => String(r.id) === selectedId);

  return (
    <>
      <Field label={`Role (${sorted.length})`}>
        <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
                style={inputStyle}>
          {sorted.map((r) => (
            <option key={r.id} value={r.id}>{roleLabel(r.name)}</option>
          ))}
        </select>
      </Field>

      {selected && (
        <div style={roleCardStyle}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <strong style={{ fontSize: '0.95rem' }}>{selected.name}</strong>
            <span style={{ color: '#999', fontSize: '0.75rem' }}>id={selected.id}</span>
            {selected.description && (
              <span style={{ color: '#666', fontSize: '0.8rem', marginLeft: 8 }}>
                — {selected.description}
              </span>
            )}
            <div style={{ flex: 1 }} />
            {onDeleteRole && (
              <button onClick={() => onDeleteRole(selected)}
                      style={{ ...smallBtnStyle, color: '#c00', borderColor: '#e3b4b4' }}
                      title="Delete role">delete role</button>
            )}
          </div>
          <div style={{ marginTop: 8 }}>
            {selected.permissions.length === 0 ? (
              <em style={{ color: '#999', fontSize: '0.85rem' }}>no permissions</em>
            ) : (
              <table style={{ ...tableStyle, marginTop: 4 }}>
                <thead>
                  <tr style={trHeadStyle}>
                    <th style={thStyle}>Task</th>
                    <th style={thStyle}>Item</th>
                    <th style={thStyle}>Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.permissions.map((p) => (
                    <tr key={p.id} style={trStyle}>
                      <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{p.task}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#555' }}>
                        {p.item_depth}={p.item_id}
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          ...badgeStyle,
                          background: p.decision === 'ALLOW' ? '#e8f4e8' : '#f4e8e8',
                          color: p.decision === 'ALLOW' ? '#2a7' : '#c44',
                          borderColor: p.decision === 'ALLOW' ? '#bcd' : '#dbc',
                        }}>{p.decision}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}


export function chatUserLabel(users, accountId) {
  const u = users && users.find((x) => x.account_id === accountId);
  if (!u) return `account #${accountId}`;
  return `${u.email || u.display_name || 'no email'} (#${accountId})`;
}


// Pick a chat user who isn't already an operator; `send(accountId)` does the request.
export function ChatUserModal({ title, intro, submitLabel, users, operators, send, onClose, onDone }) {
  const taken = new Set(operators.map((o) => o.account_id).filter((id) => id != null));
  const choices = (users || []).filter((u) => !taken.has(u.account_id) && !u.revoked);
  const [accountId, setAccountId] = useState(choices[0] ? String(choices[0].account_id) : '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!accountId) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await send(Number(accountId));
      if (!r.ok) {
        const body = await r.text();
        try { setError(JSON.parse(body).error || body); } catch (_) { setError(body || `HTTP ${r.status}`); }
        return;
      }
      onDone();
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title={title}>
      <form onSubmit={submit}>
        <p style={{ fontSize: '0.85rem', color: '#555', marginTop: 0 }}>{intro}</p>
        {users === null ? (
          <div style={{ color: '#c00', fontSize: '0.85rem', marginBottom: 12 }}>
            Listing chat users needs the manage_user_access permission.
          </div>
        ) : choices.length === 0 ? (
          <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: 12 }}>
            Every chat user is already an operator (or suspended).
          </div>
        ) : (
          <Field label="Chat user">
            <select aria-label="chat user" value={accountId}
                    onChange={(e) => setAccountId(e.target.value)} style={inputStyle}>
              {choices.map((u) => (
                <option key={u.account_id} value={u.account_id}>
                  {(u.email || 'no email')}{u.display_name ? ` — ${u.display_name}` : ''} (#{u.account_id})
                </option>
              ))}
            </select>
          </Field>
        )}
        {error && <div style={{ color: '#c00', fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} style={secondaryBtnStyle}>cancel</button>
          <button type="submit" disabled={submitting || !accountId} style={primaryBtnStyle}>
            {submitting ? 'saving…' : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}


function AssignRoleModal({ operator, roles, onClose, onAssigned }) {
  // Roles the operator already has (any live assignment) — filter out
  // so we don't show duplicates and 409.
  const heldRoleIds = new Set(operator.roles.map((r) => r.role_id));
  const availableRoles = roles.filter((r) => !heldRoleIds.has(r.id));
  const [roleId, setRoleId] = useState(availableRoles[0] ? String(availableRoles[0].id) : '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!roleId) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch('/api/admin/role_assignments', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operator_id: operator.id,
          role_id: Number(roleId),
        }),
      });
      if (!r.ok) {
        const body = await r.text();
        try {
          const j = JSON.parse(body);
          setError(j.error || body);
        } catch (_) {
          setError(`HTTP ${r.status}`);
        }
        return;
      }
      onAssigned();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title={`Assign role to ${operator.display_name}`}>
      {availableRoles.length === 0 ? (
        <div style={{ color: '#888', fontSize: '0.9rem' }}>
          {operator.display_name} already holds every role. Revoke one first
          if you want to re-assign.
        </div>
      ) : (
        <form onSubmit={submit}>
          <Field label="Role">
            <select value={roleId} onChange={(e) => setRoleId(e.target.value)}
                    required style={inputStyle}>
              {availableRoles.map((r) => (
                <option key={r.id} value={r.id}>
                  {roleLabel(r.name)} {r.description ? `— ${r.description.slice(0, 70)}${r.description.length > 70 ? '…' : ''}` : ''}
                </option>
              ))}
            </select>
          </Field>
          {error && <div style={{ color: '#c00', fontSize: '0.85rem', marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onClose} style={secondaryBtnStyle}>cancel</button>
            <button type="submit" disabled={submitting} style={primaryBtnStyle}>
              {submitting ? 'assigning…' : 'assign'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}


function Modal({ onClose, title, children }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 50,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', padding: 24, borderRadius: 8, minWidth: 360,
        maxWidth: '90vw', boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem' }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
      <span style={{ fontSize: '0.85rem', color: '#444' }}>{label}</span>
      {children}
    </label>
  );
}


function Section({ title, count, action, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem', color: '#444' }}>
          {title} <span style={{ color: '#999', fontWeight: 'normal', fontSize: '0.8rem' }}>
            ({count})
          </span>
        </h3>
        {action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div style={{ color: '#888', fontSize: '0.85rem' }}>{children}</div>;
}

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const trHeadStyle = { borderBottom: '1px solid #ddd', textAlign: 'left' };
const trStyle = { borderBottom: '1px solid #eee' };
const thStyle = { padding: '6px 8px', fontWeight: 'normal', color: '#666' };
const tdStyle = { padding: '6px 8px', verticalAlign: 'top' };
const badgeStyle = {
  display: 'inline-block', padding: '1px 6px', marginRight: 2,
  background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 3,
  fontSize: '0.7rem', fontFamily: 'monospace',
};
const refreshBtnStyle = {
  background: 'none', border: '1px solid #ccc', borderRadius: '3px',
  padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer',
};
// admin-button-color: same green as "+ Add Network" (NetworksView
// buttonPrimaryStyle) — was #222, the one black primary in the admin.
const primaryBtnStyle = {
  background: '#5fa67a', color: '#fff', border: 'none', borderRadius: 4,
  padding: '6px 14px', fontSize: '0.85rem', cursor: 'pointer',
};
const secondaryBtnStyle = {
  background: '#fff', color: '#333', border: '1px solid #ccc',
  borderRadius: 3, padding: '6px 12px', fontSize: '0.8rem', cursor: 'pointer',
};
const smallBtnStyle = {
  background: 'none', border: '1px solid #ccc', borderRadius: 3,
  padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer',
};
const revokeBtnStyle = {
  background: 'none', border: 'none', color: '#c44', cursor: 'pointer',
  fontSize: '0.8rem', padding: '0 4px', marginLeft: -2, marginRight: 2,
  verticalAlign: 'middle',
};
const inputStyle = {
  padding: '6px 10px', border: '1px solid #ccc', borderRadius: 3,
  fontSize: '0.9rem',
};
const roleCardStyle = {
  border: '1px solid #ddd', borderRadius: 4, padding: 12, background: '#fff',
};
